/* eslint-disable indent */
import { FC, useEffect, useState } from "react"
import { useRouter } from "next/router"
import { Course } from "interfaces/courses"
import CourseCard from "@cs/components/courses/course-card/CourseCard"
import styles from "./CourseGrid.module.scss"
import { CRASH_COURSE_ID } from "@cs/lib/firebase/courses-v6/const"
import { NavigationItems } from "../../navigation/Navigation"
import { useProgress } from "@cs/lib/progress-v6/ProgressContext"
import Image from "next/dist/client/image"
import SearchEarthly from "@cs/public/img/earthlies/search_earthly.png"
import { useTranslation } from "next-i18next"

const CourseGrid: FC<{ courses: Course[] }> = ({ courses }) => {
  const [content, setContent] = useState<JSX.Element | JSX.Element[]>([])
  const router = useRouter()
  const { progress } = useProgress()
  const { t } = useTranslation("courses")

  useEffect(() => {
    const difficulty = router.query.difficulty ? router.query.difficulty : "both"
    const navigation = router.query.courses as NavigationItems
    const tags = router.query.tags as string[]

    // filter crash course out
    let filteredCourses = courses.filter(course => course.id != CRASH_COURSE_ID)

    // filter by difficulty
    filteredCourses = filteredCourses.filter(course => {
      if (difficulty == "both") return true
      const hasSimpleChapters = course.simpleChapters.length > 0
      const hasAdvancedChapters = course.advancedChapters.length > 0
      const courseDifficulty =
        hasSimpleChapters && hasAdvancedChapters
          ? "both"
          : hasSimpleChapters
          ? "simple"
          : "advanced"

      return courseDifficulty == difficulty || courseDifficulty == "both"
    })

    // filter by tags
    if (tags) {
      filteredCourses = filteredCourses.filter(course =>
        course.tags.some(tag => tags.includes(tag)),
      )
    }

    // filter by navigation
    switch (navigation) {
      case "in-progress":
        filteredCourses = courses.filter(
          course =>
            !!progress.courses?.[course.id]?.startedAt &&
            !progress.courses?.[course.id]?.completedAt,
        )
        break
      case "completed":
        filteredCourses = courses.filter(course => !!progress.courses?.[course.id]?.completedAt)
        break
    }

    setContent(
      filteredCourses.length ? (
        filteredCourses.map((course, key) => <CourseCard key={key} isRelatedCourse {...course} />)
      ) : (
        <div className={styles.placeholder}>
          <figure>
            <Image
              src={SearchEarthly}
              width={1}
              height={1}
              layout="responsive"
              alt="Search earthly"
              quality={50}
            />
          </figure>
          <h2 className={styles.h2}>
            {t("knowledge-is-power")}
          </h2>
          <p className={styles.p}>
            {t("learn-about-climate-change")}
          </p>
        </div>
      )
    )
  }, [router.query])

  return <section className={styles.container}>{content}</section>
}

export default CourseGrid
